import { useState } from 'react';
import { useCookieConsent, CookieConsent } from '@/contexts/CookieConsentContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Shield, BarChart3, Target, Settings2 } from 'lucide-react';

export function CookieSettingsModal() {
  const { showSettings, closeSettings, consent, updateConsent } = useCookieConsent();
  const [localConsent, setLocalConsent] = useState<CookieConsent>(
    consent || {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    }
  );

  const handleSave = () => {
    updateConsent(localConsent);
  };

  const cookieCategories = [
    {
      id: 'necessary' as keyof CookieConsent,
      title: 'Necessary Cookies',
      description: 'These cookies are essential for the website to function properly. They cannot be disabled.',
      icon: Shield,
      required: true,
    },
    {
      id: 'analytics' as keyof CookieConsent,
      title: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website by collecting anonymous information.',
      icon: BarChart3,
      required: false,
    },
    {
      id: 'marketing' as keyof CookieConsent,
      title: 'Marketing Cookies',
      description: 'These cookies are used to display personalized advertisements and measure advertising effectiveness.',
      icon: Target,
      required: false,
    },
    {
      id: 'preferences' as keyof CookieConsent,
      title: 'Preference Cookies',
      description: 'These cookies remember your choices and preferences to provide a more personalized experience.',
      icon: Settings2,
      required: false,
    },
  ];

  return (
    <Dialog open={showSettings} onOpenChange={closeSettings}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cookie Settings</DialogTitle>
          <DialogDescription>
            Manage your cookie preferences. You can enable or disable different types of cookies below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {cookieCategories.map((category, index) => {
            const Icon = category.icon;
            return (
              <div key={category.id}>
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex items-start space-x-3 flex-1">
                    <Icon className="h-5 w-5 text-primary mt-1 shrink-0" />
                    <div className="space-y-1">
                      <Label htmlFor={category.id} className="text-sm font-medium leading-none">
                        {category.title}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {category.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id={category.id}
                    checked={localConsent[category.id]}
                    onCheckedChange={(checked) =>
                      setLocalConsent(prev => ({ ...prev, [category.id]: checked }))
                    }
                    disabled={category.required}
                  />
                </div>
                {index < cookieCategories.length - 1 && <Separator className="mt-4" />}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
          <Button variant="outline" onClick={closeSettings} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} className="flex-1">
            Save Preferences
          </Button>
        </div>

        <div className="text-xs text-muted-foreground pt-2 border-t">
          <p>
            For more information about how we use cookies, please read our{' '}
            <a href="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}